import LecturerLayout from '../../components/LecturerLayout';
import NotifyHodButton from '../../components/NotifyHodButton';
import { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import { getImageURL } from '../../utils/api';

const formatHours = (hours) => {
  if (!Number.isFinite(hours) || hours <= 0) {
    return '0h';
  }
  const rounded = Math.round(hours * 10) / 10;
  const display = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
  return `${display}h`;
};

const formatPercent = (value) => {
  if (!Number.isFinite(value) || value <= 0) {
    return '0%';
  }
  const rounded = Math.round(value * 10) / 10;
  const display = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
  return `${display}%`;
};

const formatDayCount = (value) => {
  if (!Number.isFinite(value) || value <= 0) {
    return '0 days';
  }
  const rounded = Math.round(value * 10) / 10;
  if (rounded === 1) {
    return '1 day';
  }
  const display = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
  return `${display} days`;
};

const formatDateTime = (date) => {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
  }).format(date);
};

const formatStatusLabel = (status) => {
  if (!status) {
    return 'Unknown';
  }
  return status
    .split(' ')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');
};

const getAttendanceRating = (percent) => {
  if (percent === null || percent === undefined) return null;
  if (percent >= 80) return { label: 'Excellent', style: 'bg-emerald-100 text-emerald-800 border border-emerald-300', bar: 'bg-emerald-500', tip: 'Outstanding attendance — keep it up!' };
  if (percent >= 65) return { label: 'Good', style: 'bg-sky-100 text-sky-800 border border-sky-300', bar: 'bg-sky-500', tip: 'Good attendance — a small push can make it excellent.' };
  if (percent >= 50) return { label: 'Average', style: 'bg-amber-100 text-amber-800 border border-amber-300', bar: 'bg-amber-400', tip: 'Average attendance — engage students more to improve.' };
  return { label: 'Poor', style: 'bg-rose-100 text-rose-800 border border-rose-300', bar: 'bg-rose-500', tip: 'Poor attendance — urgent attention needed to re-engage students.' };
};

const getSubjectStatusStyles = (status) => {
  const normalized = (status || '').toLowerCase();
  switch (normalized) {
    case 'ongoing':
      return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
    case 'completed':
      return 'bg-sky-100 text-sky-700 border border-sky-200';
    case 'not started':
      return 'bg-amber-100 text-amber-700 border border-amber-200';
    default:
      return 'bg-gray-100 text-gray-700 border border-gray-200';
  }
};

const getProgressGradient = (percent, overrun) => {
  if (overrun) {
    return 'from-rose-500 via-rose-500 to-rose-600';
  }
  if (percent <= 25) {
    return 'from-emerald-500 to-emerald-600';
  }
  if (percent <= 50) {
    return 'from-amber-400 to-amber-500';
  }
  if (percent <= 75) {
    return 'from-orange-500 to-orange-600';
  }
  return 'from-amber-600 to-amber-700';
};

const formatTime = (time) => {
  if (!time) {
    return 'N/A';
  }

  const parts = String(time).split(':');
  if (parts.length < 2) {
    return time;
  }

  let hours = parseInt(parts[0], 10);
  const minutes = parts[1] ?? '00';
  if (!Number.isFinite(hours)) {
    return time;
  }

  const suffix = hours >= 12 ? 'PM' : 'AM';
  if (hours === 0) {
    hours = 12;
  } else if (hours > 12) {
    hours -= 12;
  }

  const paddedHours = String(hours).padStart(2, '0');
  return `${paddedHours}:${minutes} ${suffix}`;
};

const formatScheduleDate = (value) => {
  if (!value) {
    return 'N/A';
  }

  const datePart = String(value).split('T')[0];
  const segments = datePart.split('-');
  if (segments.length !== 3) {
    return value;
  }

  const [yearStr, monthStr, dayStr] = segments;
  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1;
  const day = Number(dayStr);

  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || !Number.isFinite(day) || monthIndex < 0 || monthIndex > 11) {
    return value;
  }

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const prettyDay = String(day);
  const prettyMonthName = monthNames[monthIndex];
  const dd = String(day).padStart(2, '0');
  const mm = String(monthIndex + 1).padStart(2, '0');

  return `${prettyDay} ${prettyMonthName} ${year} (${dd}/${mm}/${year})`;
};

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [staffPendingCorrections, setStaffPendingCorrections] = useState([]);
  const [staffCorrectedLast30Days, setStaffCorrectedLast30Days] = useState([]);
  const [staffCorrectedToday, setStaffCorrectedToday] = useState([]);
  const [staffFeedbackHoursLast30Days, setStaffFeedbackHoursLast30Days] = useState([]);
  const [staffAvgCorrectionDaysLast30Days, setStaffAvgCorrectionDaysLast30Days] = useState([]);
  const [subjectHourSummary, setSubjectHourSummary] = useState([]);
  const [staff, setStaff] = useState(null);
  const [userRole, setUserRole] = useState('Staff');
  const [currentDateTime, setCurrentDateTime] = useState(() => formatDateTime(new Date()));
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [hoursDetail, setHoursDetail] = useState(null);
  const [hoursDetailLoading, setHoursDetailLoading] = useState(false);
  const [hoursDetailError, setHoursDetailError] = useState(null);
  const [isHoursDetailOpen, setIsHoursDetailOpen] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const now = new Date();
        const localDate = `${now.getFullYear()}/${now.getMonth() + 1}/${now.getDate()}`;
        const resp = await axiosInstance.get('/admin/lecturer/dashboard', { params: { today: localDate } });
        if (!mounted) return;

        const data = resp?.data || {};
        setStaffPendingCorrections(data.staffPendingCorrections || []);
        setStaffCorrectedLast30Days(data.staffCorrectedLast30Days || []);
        setStaffCorrectedToday(data.staffCorrectedToday || []);
        setStaffFeedbackHoursLast30Days(data.staffFeedbackHoursLast30Days || []);
        setStaffAvgCorrectionDaysLast30Days(data.staffAvgCorrectionDaysLast30Days || []);
        setSubjectHourSummary(data.subjectHourSummary || []);

        const user = AuthService.getUser();
        const staffFromUser = user?.superAdmin?.staff || null;
        setStaff(staffFromUser);
        const roleFromUser = user?.role || staffFromUser?.type || 'Staff';
        setUserRole(roleFromUser);

        setLoading(false);
      } catch (e) {
        if (!mounted) return;
        setError(e?.response?.data?.message || 'Failed to load lecturer dashboard');
        setLoading(false);
      }
    };

    fetchData();

    const interval = window.setInterval(() => {
      setCurrentDateTime(formatDateTime(new Date()));
    }, 1000);

    return () => {
      mounted = false;
      window.clearInterval(interval);
    };
  }, []);

  const orderedSubjects = useMemo(() => {
    if (!subjectHourSummary) {
      return [];
    }
    return subjectHourSummary
      .flatMap((group) =>
        group.subjects.map((subject) => ({
          ...subject,
          groupName: group.groupName,
        }))
      )
      .sort((a, b) => b.progressPercent - a.progressPercent);
  }, [subjectHourSummary]);

  // Merge subjects that share a batchGroupId into a single grouped card
  const displayItems = useMemo(() => {
    const items = [];
    const batchGroupIndexMap = new Map(); // batchGroupId → index in items

    for (const subject of orderedSubjects) {
      if (subject.batchGroupId) {
        if (batchGroupIndexMap.has(subject.batchGroupId)) {
          items[batchGroupIndexMap.get(subject.batchGroupId)].groupSubjects.push(subject);
        } else {
          batchGroupIndexMap.set(subject.batchGroupId, items.length);
          items.push({ type: 'group', batchGroupId: subject.batchGroupId, batchGroupName: subject.batchGroupName, groupSubjects: [subject] });
        }
      } else {
        items.push({ type: 'solo', subject });
      }
    }

    // Compute merged attendance stats for grouped items
    for (const item of items) {
      if (item.type !== 'group') continue;
      const subs = item.groupSubjects;
      item.totalStudents = subs.reduce((s, sub) => s + (sub.totalStudents || 0), 0);
      const totalPresent = subs.reduce((s, sub) => s + (sub.totalPresent || 0), 0);
      const classesWithAttendance = Math.max(...subs.map((sub) => sub.classesWithAttendance || 0), 0);
      item.avgAttendingStudents =
        classesWithAttendance > 0 ? Math.round((totalPresent / classesWithAttendance) * 10) / 10 : null;
      item.avgAttendancePercent =
        item.avgAttendingStudents !== null && item.totalStudents > 0
          ? Math.round((item.avgAttendingStudents / item.totalStudents) * 1000) / 10
          : null;
    }

    return items;
  }, [orderedSubjects]);

  const visibleAvgCorrectionDays = useMemo(() => {
    if (!staffAvgCorrectionDaysLast30Days) {
      return [];
    }

    return staffAvgCorrectionDaysLast30Days.filter((item) => (Number(item.avg_days) || 0) > 0);
  }, [staffAvgCorrectionDaysLast30Days]);

  const isVisitingLecturer = /visiting/i.test(staff?.positionName || '');

  const getRoleColor = (role) => {
    const colorMap = {
      Admin: 'bg-purple-100 text-purple-800',
      Administrator: 'bg-fuchsia-100 text-fuchsia-800',
      'Front Office Manager': 'bg-blue-100 text-blue-800',
      Lecturers: 'bg-green-100 text-green-800',
      'Visiting Lecturer': 'bg-emerald-100 text-emerald-800',
      'HND Coordination': 'bg-indigo-100 text-indigo-800',
      'BEng Coordination': 'bg-violet-100 text-violet-800',
      'Student Counselor': 'bg-rose-100 text-rose-800',
      'Front Team': 'bg-sky-100 text-sky-800',
      Management: 'bg-amber-100 text-amber-800',
      'HND Supervisor': 'bg-teal-100 text-teal-800',
      'Recovery Officer': 'bg-orange-100 text-orange-800',
      'BM Coordination': 'bg-lime-100 text-lime-800',
      'Diploma IT Coordination': 'bg-cyan-100 text-cyan-800',
      'Diploma English Coordination': 'bg-pink-100 text-pink-800',
    };
    return colorMap[role] || 'bg-gray-100 text-gray-800';
  };

  const openHoursDetail = async (subject) => {
    if (!subject?.batchSubjectId) return;
    setSelectedSubject(subject);
    setIsHoursDetailOpen(true);
    setHoursDetail(null);
    setHoursDetailError(null);
    setHoursDetailLoading(true);
    try {
      const resp = await axiosInstance.get(`/admin/lecturer/delivery-hours/${subject.batchSubjectId}`);
      setHoursDetail(resp?.data || null);
    } catch (e) {
      setHoursDetailError(e?.response?.data?.message || 'Failed to load schedule details.');
    } finally {
      setHoursDetailLoading(false);
    }
  };

  const closeHoursDetail = () => {
    setIsHoursDetailOpen(false);
    setSelectedSubject(null);
    setHoursDetail(null);
    setHoursDetailError(null);
    setHoursDetailLoading(false);
  };

  return (
    <LecturerLayout title="Dashboard">
      {error && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {error}
        </div>
      )}
      {loading && (
        <div className="mb-4 text-sm text-gray-500">Loading lecturer dashboard...</div>
      )}
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 p-6 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Welcome back, {staff?.name || 'User'}!
              </h1>
              <p className="mt-2 text-gray-600">Here's your dashboard overview for today.</p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <NotifyHodButton />
              <div className="flex items-center gap-3 rounded-lg bg-white/70 px-4 py-3 text-sm text-gray-700 shadow-sm">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Current Time</p>
                  <p className="text-sm font-semibold text-gray-900">{currentDateTime}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg bg-white shadow-lg">
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
            <h2 className="text-lg font-semibold text-white">Profile Information</h2>
          </div>

          <div className="p-6">
            <div className="flex flex-col items-center md:flex-row md:items-start md:space-x-6">
              <div className="flex-shrink-0">
                {staff?.imagePath ? (
                  <img
                    src={getImageURL(staff.imagePath)}
                    alt={staff?.name || 'User'}
                    className="h-32 w-32 rounded-full border-4 border-green-100 object-cover shadow-md"
                  />
                ) : (
                  <div className="flex h-32 w-32 items-center justify-center rounded-full border-4 border-green-100 bg-gradient-to-br from-green-500 to-emerald-600 text-4xl font-bold text-white shadow-md">
                    {staff?.name
                      ?.split(' ')
                      .map((n) => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2) || 'U'}
                  </div>
                )}
              </div>

              <div className="mt-6 flex-1 text-center md:mt-0 md:text-left">
                <div className="mb-3 flex flex-col items-center space-y-2 md:flex-row md:items-center md:space-x-3 md:space-y-0">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {staff?.title && `${staff.title} `}
                    {staff?.name || 'User Name'}
                  </h3>
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${getRoleColor(
                      userRole
                    )}`}
                  >
                    {userRole}
                  </span>
                </div>

                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div className="flex items-start space-x-3 rounded-lg bg-gray-50 p-3">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"
                        />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-500">Employee Number</p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">{staff?.employeeNumber || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 rounded-lg bg-gray-50 p-3">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-500">Position</p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">{staff?.positionName || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 rounded-lg bg-gray-50 p-3">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-500">Office Email</p>
                      <p className="mt-1 truncate text-sm font-semibold text-gray-900">{staff?.officeEmail || 'N/A'}</p>
                    </div>
                  </div>

                  {staff?.gmail && (
                    <div className="flex items-start space-x-3 rounded-lg bg-gray-50 p-3">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                          />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-500">Personal Email</p>
                        <p className="mt-1 truncate text-sm font-semibold text-gray-900">{staff.gmail}</p>
                      </div>
                    </div>
                  )}

                  {staff?.mobileNumber && (
                    <div className="flex items-start space-x-3 rounded-lg bg-gray-50 p-3">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-500">Mobile Number</p>
                        <p className="mt-1 text-sm font-semibold text-gray-900">{staff.mobileNumber}</p>
                      </div>
                    </div>
                  )}

                  {staff?.gender && (
                    <div className="flex items-start space-x-3 rounded-lg bg-gray-50 p-3">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-500">Gender</p>
                        <p className="mt-1 text-sm font-semibold text-gray-900">{staff.gender}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {!isVisitingLecturer && staffPendingCorrections && staffPendingCorrections.length > 0 && (
          <div className="overflow-hidden rounded-lg bg-white shadow-lg">
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-3">
              <h2 className="text-base font-semibold text-gray-900">Staff Not Corrected Submissions</h2>
            </div>

            <div className="overflow-x-auto p-4">
              <div className="flex items-end justify-start gap-3 pb-2" style={{ minWidth: 'fit-content' }}>
                {staffPendingCorrections.map((s) => {
                  const maxCount = Math.max(...staffPendingCorrections.map((item) => item.total_pending));
                  const heightPercentage = (s.total_pending / maxCount) * 100;
                  const minHeight = 40;
                  const calculatedHeight = Math.max(minHeight, (heightPercentage / 100) * 180);

                  return (
                    <div
                      key={s.staffId}
                      className="flex flex-shrink-0 flex-col items-center space-y-1.5"
                    >
                      <StaffAvatar staffId={s.staffId} staffName={s.staffName} imagePath={s.image} />

                      <div
                        className="w-12 rounded-t-lg bg-gradient-to-t from-indigo-600 to-indigo-400 transition-all duration-300 hover:from-indigo-700 hover:to-indigo-500"
                        style={{ height: `${calculatedHeight}px` }}
                      />

                      <div className="flex h-6 w-12 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-900">
                        {s.total_pending}
                      </div>

                      <p
                        className="w-16 truncate text-center text-[10px] font-medium text-gray-700"
                        title={s.staffName}
                      >
                        {s.staffName.split(' ').slice(0, 2).join(' ')}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {!isVisitingLecturer && staffCorrectedLast30Days && staffCorrectedLast30Days.length > 0 && (
          <div className="overflow-hidden rounded-lg bg-white shadow-lg">
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-3">
              <h2 className="text-base font-semibold text-gray-900">Staff Corrected Submissions</h2>
              <p className="mt-0.5 text-xs text-gray-500">Last 30 days</p>
            </div>

            <div className="overflow-x-auto p-4">
              <div className="flex items-end justify-start gap-3 pb-2" style={{ minWidth: 'fit-content' }}>
                {staffCorrectedLast30Days.map((s) => {
                  const maxCount = Math.max(...staffCorrectedLast30Days.map((item) => item.total_corrected));
                  const heightPercentage = (s.total_corrected / maxCount) * 100;
                  const minHeight = 40;
                  const calculatedHeight = Math.max(minHeight, (heightPercentage / 100) * 180);

                  return (
                    <div
                      key={s.staffId}
                      className="flex flex-shrink-0 flex-col items-center space-y-1.5"
                    >
                      <StaffAvatar staffId={s.staffId} staffName={s.staffName} imagePath={s.image} />

                      <div
                        className="w-12 rounded-t-lg bg-gradient-to-t from-emerald-600 to-emerald-400 transition-all duration-300 hover:from-emerald-700 hover:to-emerald-500"
                        style={{ height: `${calculatedHeight}px` }}
                      />

                      <div className="flex h-6 w-12 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-900">
                        {s.total_corrected}
                      </div>

                      <p
                        className="w-16 truncate text-center text-[10px] font-medium text-gray-700"
                        title={s.staffName}
                      >
                        {s.staffName.split(' ').slice(0, 2).join(' ')}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {!isVisitingLecturer && (
        <div className="overflow-hidden rounded-lg bg-white shadow-lg">
          <div className="border-b border-gray-200 bg-blue-50 px-6 py-3">
            <h2 className="text-base font-semibold text-gray-900">Today's Assignment Corrections</h2>
            <p className="mt-0.5 text-xs text-blue-600 font-medium">
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          <div className="overflow-x-auto p-4">
            {staffCorrectedToday && staffCorrectedToday.length > 0 ? (
              <div className="flex items-end justify-start gap-3 pb-2" style={{ minWidth: 'fit-content' }}>
                {staffCorrectedToday.map((s) => {
                  const maxCount = Math.max(...staffCorrectedToday.map((item) => item.total_corrected));
                  const heightPercentage = (s.total_corrected / maxCount) * 100;
                  const minHeight = 40;
                  const calculatedHeight = Math.max(minHeight, (heightPercentage / 100) * 180);

                  return (
                    <div
                      key={s.staffId}
                      className="flex flex-shrink-0 flex-col items-center space-y-1.5"
                    >
                      <StaffAvatar staffId={s.staffId} staffName={s.staffName} imagePath={s.image} />

                      <div
                        className="w-12 rounded-t-lg bg-gradient-to-t from-blue-600 to-blue-400 transition-all duration-300 hover:from-blue-700 hover:to-blue-500"
                        style={{ height: `${calculatedHeight}px` }}
                      />

                      <div className="flex h-6 w-12 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-900">
                        {s.total_corrected}
                      </div>

                      <p
                        className="w-16 truncate text-center text-[10px] font-medium text-gray-700"
                        title={s.staffName}
                      >
                        {s.staffName.split(' ').slice(0, 2).join(' ')}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-amber-800">No corrections recorded today</p>
                  <p className="mt-0.5 text-xs text-amber-700">No staff member has corrected any assignment on {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}. Please ensure corrections are being submitted.</p>
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {!isVisitingLecturer && staffFeedbackHoursLast30Days && staffFeedbackHoursLast30Days.length > 0 && (
          <div className="overflow-hidden rounded-lg bg-white shadow-lg">
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-3">
              <h2 className="text-base font-semibold text-gray-900">Staff Feedback Hours</h2>
              <p className="mt-0.5 text-xs text-gray-500">Last 30 days — total duration</p>
            </div>

            <div className="overflow-x-auto p-4">
              <div className="flex items-end justify-start gap-3 pb-2" style={{ minWidth: 'fit-content' }}>
                {staffFeedbackHoursLast30Days.map((s) => {
                  const maxMins = Math.max(...staffFeedbackHoursLast30Days.map((item) => item.total_minutes));
                  const heightPercentage = (s.total_minutes / maxMins) * 100;
                  const minHeight = 40;
                  const calculatedHeight = Math.max(minHeight, (heightPercentage / 100) * 180);
                  const hrs = Math.floor(s.total_minutes / 60);
                  const mins = s.total_minutes % 60;
                  const label = hrs > 0 ? (mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`) : `${mins}m`;

                  return (
                    <div
                      key={s.staffId}
                      className="flex flex-shrink-0 flex-col items-center space-y-1.5"
                    >
                      <StaffAvatar staffId={s.staffId} staffName={s.staffName} imagePath={s.image} />

                      <div
                        className="w-12 rounded-t-lg bg-gradient-to-t from-amber-500 to-amber-300 transition-all duration-300 hover:from-amber-600 hover:to-amber-400"
                        style={{ height: `${calculatedHeight}px` }}
                      />

                      <div className="flex h-6 w-14 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold text-amber-900">
                        {label}
                      </div>

                      <p
                        className="w-16 truncate text-center text-[10px] font-medium text-gray-700"
                        title={s.staffName}
                      >
                        {s.staffName.split(' ').slice(0, 2).join(' ')}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {!isVisitingLecturer && visibleAvgCorrectionDays.length > 0 && (
          <div className="overflow-hidden rounded-lg bg-white shadow-lg">
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-3">
              <h2 className="text-base font-semibold text-gray-900">Avg. Assignment Correction Days</h2>
              <p className="mt-0.5 text-xs text-gray-500">Last 30 days — ordered fastest to slowest</p>
            </div>

            <div className="overflow-x-auto p-4">
              {(() => {
                const maxDays = Math.max(
                  ...visibleAvgCorrectionDays.map((item) => Number(item.avg_days) || 0),
                  0
                );

                return (
              <div className="flex items-end justify-start gap-3 pb-2" style={{ minWidth: 'fit-content' }}>
                {visibleAvgCorrectionDays.map((s) => {
                  const averageDays = Number(s.avg_days) || 0;
                  const heightPercentage = maxDays > 0 ? (averageDays / maxDays) * 100 : 100;
                  const minHeight = 40;
                  const calculatedHeight = Math.max(minHeight, (heightPercentage / 100) * 180);
                  const label = formatDayCount(averageDays);

                  return (
                    <div
                      key={s.staffId}
                      className="flex flex-shrink-0 flex-col items-center space-y-1.5"
                      title={`${s.staffName} — avg ${label} (${s.total_corrected} corrected)`}
                    >
                      <StaffAvatar staffId={s.staffId} staffName={s.staffName} imagePath={s.image} />

                      <div
                        className="w-12 rounded-t-lg bg-gradient-to-t from-emerald-500 to-emerald-300 transition-all duration-300 hover:from-emerald-600 hover:to-emerald-400"
                        style={{ height: `${calculatedHeight}px` }}
                      />

                      <div className="flex min-h-6 min-w-[4.5rem] items-center justify-center rounded-full bg-emerald-100 px-2 text-[10px] font-bold leading-none text-emerald-900">
                        {label}
                      </div>

                      <p
                        className="w-16 truncate text-center text-[10px] font-medium text-gray-700"
                        title={s.staffName}
                      >
                        {s.staffName.split(' ').slice(0, 2).join(' ')}
                      </p>
                    </div>
                  );
                })}
              </div>
                );
              })()}
            </div>
          </div>
        )}

        {subjectHourSummary && (
          <div className="overflow-hidden rounded-lg bg-white shadow-lg">
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-3">
              <h2 className="text-base font-semibold text-gray-900">Delivery Hours Progress</h2>
              <p className="mt-1 text-sm text-gray-500">
                Aim to finish each subject within the allocated hours—prioritise the ones closest to 100% completion.
              </p>
            </div>
            {displayItems.length > 0 ? (
              <div className="space-y-4 p-6">
                {displayItems.map((item) => {
                  if (item.type === 'group') {
                    // --- Grouped card: multiple batches taught together ---
                    const { batchGroupId, batchGroupName, groupSubjects, totalStudents, avgAttendingStudents, avgAttendancePercent } = item;
                    return (
                      <div
                        key={`batchgroup-${batchGroupId}`}
                        className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-4 shadow-inner"
                      >
                        {/* Group header */}
                        <div className="mb-3 flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-[11px] font-semibold text-indigo-700">
                            Combined class
                          </span>
                          {batchGroupName && (
                            <span className="text-sm font-semibold text-gray-800">{batchGroupName}</span>
                          )}
                        </div>

                        {/* Per-batch progress rows */}
                        <div className="space-y-3">
                          {groupSubjects.map((subject) => (
                            <div key={subject.batchSubjectId} className="rounded-md border border-gray-100 bg-white p-3">
                              <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">
                                    {subject.cohortLabel || subject.groupName || 'No WhatsApp Group'}
                                  </p>
                                  {subject.batchName && subject.batchName !== subject.cohortLabel && (
                                    <p className="mt-0.5 text-[11px] text-gray-400">Batch: {subject.batchName}</p>
                                  )}
                                  <p className="mt-1 text-xs text-gray-500">
                                    {subject.subjectName}
                                    {subject.subjectCode && subject.subjectCode !== subject.subjectName && (
                                      <span className="ml-2 text-gray-400">({subject.subjectCode})</span>
                                    )}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${getSubjectStatusStyles(subject.status)}`}
                                  >
                                    {formatStatusLabel(subject.status)}
                                  </span>
                                  <span
                                    className={`text-xs font-semibold ${subject.overrunHours > 0 ? 'text-rose-600' : 'text-emerald-600'}`}
                                  >
                                    {formatPercent(subject.progressPercent)}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => openHoursDetail(subject)}
                                    className="inline-flex items-center rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-[11px] font-medium text-emerald-700 shadow-sm hover:border-emerald-300 hover:bg-emerald-50"
                                  >
                                    View schedule
                                  </button>
                                </div>
                              </div>
                              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100">
                                <div
                                  className={`h-full bg-gradient-to-r ${getProgressGradient(subject.progressPercent, subject.overrunHours > 0)}`}
                                  style={{ width: `${Math.min(100, Math.max(0, subject.cappedProgressPercent ?? subject.progressPercent))}%` }}
                                />
                              </div>
                              <div className="mt-1.5 flex flex-wrap items-center gap-4 text-xs text-gray-600">
                                <span>{formatHours(subject.scheduledHours)} delivered</span>
                                {subject.overrunHours > 0 ? (
                                  <span className="font-semibold text-rose-600">Over by {formatHours(subject.overrunHours)}</span>
                                ) : (
                                  <span>{formatHours(subject.remainingHours)} remaining</span>
                                )}
                                <span>Planned {formatHours(subject.expectedHours)} ({subject.expectedClasses} classes)</span>
                              </div>
                              {subject.overrunHours > 0 && (
                                <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                                  Delivery has exceeded the allocated hours. Review schedule to realign with the plan.
                                </div>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Combined attendance row */}
                        {(() => {
                          const rating = getAttendanceRating(avgAttendancePercent);
                          return (
                            <div className="mt-3 rounded-md border border-gray-200 bg-white px-4 py-3">
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-700">
                                  <span className="font-semibold text-gray-800">Combined Attendance</span>
                                  <span>
                                    <span className="font-semibold text-gray-900">{totalStudents ?? 0}</span> enrolled
                                  </span>
                                  {avgAttendingStudents !== null && avgAttendingStudents !== undefined ? (
                                    <span>
                                      avg <span className="font-semibold text-gray-900">{avgAttendingStudents}</span> attending per class
                                    </span>
                                  ) : (
                                    <span className="italic text-gray-400">attendance not recorded yet</span>
                                  )}
                                  {avgAttendancePercent !== null && avgAttendancePercent !== undefined && (
                                    <span className="font-semibold text-gray-900">{avgAttendancePercent}%</span>
                                  )}
                                </div>
                                {rating && (
                                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${rating.style}`}>
                                    {rating.label}
                                  </span>
                                )}
                              </div>
                              {avgAttendancePercent !== null && avgAttendancePercent !== undefined && (
                                <>
                                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                                    <div
                                      className={`h-full rounded-full transition-all duration-500 ${rating ? rating.bar : 'bg-gray-400'}`}
                                      style={{ width: `${Math.min(100, Math.max(0, avgAttendancePercent))}%` }}
                                    />
                                  </div>
                                  {rating && (
                                    <p className="mt-1.5 text-[11px] text-gray-500 italic">{rating.tip}</p>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  }

                  // --- Solo card ---
                  const subject = item.subject;
                  return (
                    <div
                      key={subject.batchSubjectId}
                      className="rounded-lg border border-gray-100 bg-gray-50 p-4 shadow-inner"
                    >
                      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {subject.cohortLabel || subject.groupName || 'No WhatsApp Group'}
                          </p>
                          {subject.batchName && subject.batchName !== subject.cohortLabel && (
                            <p className="mt-0.5 text-[11px] text-gray-400">Batch: {subject.batchName}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-500">
                            {subject.subjectName}
                            {subject.subjectCode && subject.subjectCode !== subject.subjectName && (
                              <span className="ml-2 text-gray-400">({subject.subjectCode})</span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${getSubjectStatusStyles(subject.status)}`}
                          >
                            {formatStatusLabel(subject.status)}
                          </span>
                          <span
                            className={`text-xs font-semibold ${subject.overrunHours > 0 ? 'text-rose-600' : 'text-emerald-600'}`}
                          >
                            {formatPercent(subject.progressPercent)}
                          </span>
                          <button
                            type="button"
                            onClick={() => openHoursDetail(subject)}
                            className="inline-flex items-center rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-[11px] font-medium text-emerald-700 shadow-sm hover:border-emerald-300 hover:bg-emerald-50"
                          >
                            View schedule
                          </button>
                        </div>
                      </div>
                      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white">
                        <div
                          className={`h-full bg-gradient-to-r ${getProgressGradient(subject.progressPercent, subject.overrunHours > 0)}`}
                          style={{ width: `${Math.min(100, Math.max(0, subject.cappedProgressPercent ?? subject.progressPercent))}%` }}
                        />
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-gray-600">
                        <span>{formatHours(subject.scheduledHours)} delivered</span>
                        {subject.overrunHours > 0 ? (
                          <span className="font-semibold text-rose-600">Over by {formatHours(subject.overrunHours)}</span>
                        ) : (
                          <span>{formatHours(subject.remainingHours)} remaining</span>
                        )}
                        <span>Planned {formatHours(subject.expectedHours)} ({subject.expectedClasses} classes)</span>
                      </div>
                      {(() => {
                        const rating = getAttendanceRating(subject.avgAttendancePercent);
                        return (
                          <div className="mt-2 rounded-md border border-gray-200 bg-white px-4 py-3">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-700">
                                <span className="font-semibold text-gray-800">Student Attendance</span>
                                <span>
                                  <span className="font-semibold text-gray-900">{subject.totalStudents ?? 0}</span> enrolled
                                </span>
                                {subject.avgAttendingStudents !== null && subject.avgAttendingStudents !== undefined ? (
                                  <span>
                                    avg <span className="font-semibold text-gray-900">{subject.avgAttendingStudents}</span> attending per class
                                  </span>
                                ) : (
                                  <span className="italic text-gray-400">attendance not recorded yet</span>
                                )}
                                {subject.avgAttendancePercent !== null && subject.avgAttendancePercent !== undefined && (
                                  <span className="font-semibold text-gray-900">{subject.avgAttendancePercent}%</span>
                                )}
                              </div>
                              {rating && (
                                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${rating.style}`}>
                                  {rating.label}
                                </span>
                              )}
                            </div>
                            {subject.avgAttendancePercent !== null && subject.avgAttendancePercent !== undefined && (
                              <>
                                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                                  <div
                                    className={`h-full rounded-full transition-all duration-500 ${rating ? rating.bar : 'bg-gray-400'}`}
                                    style={{ width: `${Math.min(100, Math.max(0, subject.avgAttendancePercent))}%` }}
                                  />
                                </div>
                                {rating && (
                                  <p className="mt-1.5 text-[11px] text-gray-500 italic">{rating.tip}</p>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })()}
                      {subject.overrunHours > 0 && (
                        <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                          Delivery has exceeded the allocated hours. Review schedule to realign with the plan.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-6 text-sm text-gray-500">No ongoing subjects assigned at the moment.</div>
            )}
          </div>
        )}
      </div>

      {isHoursDetailOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4 py-6">
          <div className="max-h-[80vh] w-full max-w-3xl overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 sm:px-6">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Schedule hours detail</h3>
                {selectedSubject && (
                  <p className="mt-1 text-xs text-gray-500">
                    {selectedSubject.cohortLabel || selectedSubject.groupName || 'No WhatsApp Group'} ·{' '}
                    {selectedSubject.subjectName}
                    {selectedSubject.subjectCode && selectedSubject.subjectCode !== selectedSubject.subjectName && (
                      <span className="ml-1 text-gray-400">({selectedSubject.subjectCode})</span>
                    )}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={closeHoursDetail}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                <span className="sr-only">Close</span>
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-3 border-b border-gray-100 px-4 py-3 text-xs text-gray-600 sm:px-6">
              {hoursDetailLoading && <p>Loading schedule details...</p>}
              {hoursDetailError && !hoursDetailLoading && (
                <p className="text-rose-600">{hoursDetailError}</p>
              )}
              {hoursDetail && !hoursDetailLoading && (
                <div className="flex flex-wrap items-center gap-4">
                  {hoursDetail.staffName && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-gray-500">Lecturer</p>
                      <p className="text-sm font-semibold text-gray-900">{hoursDetail.staffName}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Planned hours</p>
                    <p className="text-sm font-semibold text-gray-900">{formatHours(hoursDetail.expectedHours)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Total from schedule</p>
                    <p className="text-sm font-semibold text-gray-900">{formatHours(hoursDetail.totalHours)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Total percentage</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatPercent(
                        hoursDetail.expectedHours > 0
                          ? (hoursDetail.totalHours / hoursDetail.expectedHours) * 100
                          : 0
                      )}
                    </p>
                  </div>
                  {selectedSubject && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-gray-500">Dashboard delivered</p>
                      <p className="text-sm font-semibold text-gray-900">{formatHours(selectedSubject.scheduledHours)}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="max-h-[55vh] overflow-y-auto px-4 py-3 sm:px-6">
              {hoursDetail && hoursDetail.sessions && hoursDetail.sessions.length > 0 && !hoursDetailLoading ? (
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-2 py-2 text-left font-semibold text-gray-700">No</th>
                      <th scope="col" className="px-2 py-2 text-left font-semibold text-gray-700">Date</th>
                      <th scope="col" className="px-2 py-2 text-left font-semibold text-gray-700">Time</th>
                      <th scope="col" className="px-2 py-2 text-left font-semibold text-gray-700">Hours</th>
                      <th scope="col" className="px-2 py-2 text-left font-semibold text-gray-700">Classroom</th>
                      <th scope="col" className="px-2 py-2 text-left font-semibold text-gray-700">Batch / Group</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {hoursDetail.sessions.map((session, index) => (
                      <tr key={session.scheduleId || `${session.date}-${session.startTime}-${session.endTime}`}>
                        <td className="px-2 py-1 align-top text-gray-900">{index + 1}</td>
                        <td className="px-2 py-1 align-top text-gray-900">
                          {session.date ? formatScheduleDate(session.date) : 'N/A'}
                        </td>
                        <td className="px-2 py-1 align-top text-gray-700">
                          {session.startTime && session.endTime
                            ? `${formatTime(session.startTime)} · ${formatTime(session.endTime)}`
                            : 'N/A'}
                        </td>
                        <td className="px-2 py-1 align-top text-gray-900">{formatHours(session.hours)}</td>
                        <td className="px-2 py-1 align-top text-gray-700">{session.classroomName || '-'}</td>
                        <td className="px-2 py-1 align-top text-gray-700">
                          {session.whatsappGroupName || session.batchName || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}

              {!hoursDetailLoading && hoursDetail && hoursDetail.sessions && hoursDetail.sessions.length === 0 && (
                <p className="text-xs text-gray-500">No scheduled classes found for this subject.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </LecturerLayout>
  );
}

function StaffAvatar({ staffId, staffName, imagePath }) {
  if (imagePath) {
    return (
      <img
        src={getImageURL(imagePath)}
        alt={staffName}
        className="h-12 w-12 rounded-full border-2 border-indigo-200 object-cover shadow-sm"
      />
    );
  }

  const initials = staffName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-indigo-200 bg-gradient-to-br from-indigo-500 to-indigo-700 text-sm font-bold text-white shadow-sm">
      {initials}
    </div>
  );
}
